import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { createGlobalStyle } from 'styled-components';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircleOutline as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  EventOutlined as EventIcon,
  HourglassEmpty as PendingIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  PrintOutlined as PrintIcon,
} from '@mui/icons-material';
import { useAxios } from '../../hooks/use-axios';
import { baseUrls, eventUrl } from '../../constants';
import { useAuthContext } from '../../context/auth-context';

const PrintStyles = createGlobalStyle`
  @media print {
    body * { visibility: hidden; }
    #printable-content, #printable-content * { visibility: visible; }
    #printable-content {
      position: absolute;
      left: 0; top: 0;
      width: 100%;
      padding: 0 10px;
    }
    table { width: 100% !important; font-size: 10px !important; }
    th, td { padding: 4px !important; font-size: 10px !important; word-wrap: break-word; }
    @page { size: landscape; margin: 0.5cm; }
  }
`;

const EventCard = ({ event, selected, onClick, disabled }) => (
  <Card
    onClick={disabled ? undefined : onClick}
    sx={{
      mb: 1.5,
      cursor: disabled ? 'default' : 'pointer',
      backgroundColor: selected ? '#C1EFD0' : 'background.paper',
      border: selected ? '1.5px solid #284B32' : '1.5px solid transparent',
      transition: 'background-color 0.15s, border-color 0.15s',
      opacity: disabled ? 0.6 : 1,
    }}
  >
    <CardActionArea sx={{ p: 0 }} disabled={disabled}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <EventIcon sx={{ flexShrink: 0, color: selected ? '#284B32' : 'action.active', fontSize: 20 }} />
        <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 600, lineHeight: 1.3, color: selected ? '#284B32' : 'text.primary' }}>
          {event.name}
        </Typography>
      </Box>
    </CardActionArea>
  </Card>
);

/* ── Desktop: panel de detalle con tabla expandible ── */
const ReportDetail = ({ selectedEvent, reportData, activeCamp }) => {
  const [zoneFilter, setZoneFilter] = useState('');
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    setZoneFilter('');
    setExpandedRows({});
  }, [selectedEvent?._id]);

  const getUniqueZones = () =>
    [...new Set((reportData?.results || []).map(c => c.affiliationZone).filter(Boolean))].sort();

  const filteredResults = (reportData?.results || []).filter(club =>
    !zoneFilter || club.affiliationZone === zoneFilter
  );

  const toggleRow = (clubId) =>
    setExpandedRows(prev => ({ ...prev, [clubId]: !prev[clubId] }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap', flexShrink: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#284B32', flex: 1 }}>
          {selectedEvent.name}
        </Typography>
        {getUniqueZones().length > 0 && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Zona</InputLabel>
            <Select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} label="Zona">
              <MenuItem value=""><em>Todas</em></MenuItem>
              {getUniqueZones().map(z => <MenuItem key={z} value={z}>{z}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}
          sx={{ backgroundColor: '#284B32', '&:hover': { backgroundColor: '#1a3620' } }}>
          Imprimir
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mb: 2, flexShrink: 0 }}>
        {[
          { label: 'Clubes', value: zoneFilter ? filteredResults.length : reportData.summary.totalClubs },
          { label: 'Puntos totales', value: reportData.summary.totalGrades },
          { label: 'Promedio', value: reportData.summary.averageGrade },
        ].map(m => (
          <Box key={m.label}>
            <Typography variant="caption" color="text.secondary" display="block">{m.label}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#284B32', lineHeight: 1 }}>{m.value}</Typography>
          </Box>
        ))}
      </Box>

      <Divider sx={{ mb: 2, flexShrink: 0 }} />

      <Box id="printable-content" sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'none', textAlign: 'center', mb: 2, '@media print': { display: 'block' } }}>
          <Typography variant="h4" gutterBottom>Calificaciones por Evento</Typography>
          <Typography variant="h6" gutterBottom>{activeCamp?.name || ''}</Typography>
          <Typography variant="subtitle1" gutterBottom>{selectedEvent.name}</Typography>
          <Typography variant="body1" gutterBottom>
            Total de Clubes: {reportData.summary.totalClubs} | Puntos Totales: {reportData.summary.totalGrades} | Promedio: {reportData.summary.averageGrade}
          </Typography>
        </Box>

        <TableContainer component={Paper} sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>#</TableCell>
                <TableCell>Club</TableCell>
                <TableCell>Afiliación</TableCell>
                <TableCell>Zona</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredResults.map((club, index) => (
                <React.Fragment key={club.clubId}>
                  <TableRow hover onClick={() => toggleRow(club.clubId)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <IconButton size="small">
                        {expandedRows[club.clubId] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell><strong>{club.clubName}</strong></TableCell>
                    <TableCell>{club.affiliationName || '-'}</TableCell>
                    <TableCell>{club.affiliationZone || '-'}</TableCell>
                    <TableCell align="right"><strong>{club.totalGrade}</strong></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                      <Collapse in={expandedRows[club.clubId]} timeout="auto" unmountOnExit>
                        <Box sx={{ mx: 2, my: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#284B32', mb: 1 }}>
                            Detalle de Rúbricas
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Rúbrica</TableCell>
                                <TableCell align="right">Máx. puntos</TableCell>
                                <TableCell align="right">Calificación</TableCell>
                                <TableCell>Calificado por</TableCell>
                                <TableCell>Comentarios</TableCell>
                                <TableCell align="center">Estado</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {club.rubricGrades.map((rubric, idx) => (
                                <TableRow key={idx} sx={{ bgcolor: '#fafafa' }}>
                                  <TableCell>{rubric.rubricName}</TableCell>
                                  <TableCell align="right">{rubric.maxPoints}</TableCell>
                                  <TableCell align="right">{rubric.grade}</TableCell>
                                  <TableCell>{rubric.savedBy || '-'}</TableCell>
                                  <TableCell>{rubric.comments || '-'}</TableCell>
                                  <TableCell align="center">
                                    {rubric.isGraded
                                      ? <Chip label="Calificado" color="success" size="small" />
                                      : <Chip label="Pendiente" color="warning" size="small" />
                                    }
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

/* ── Móvil paso 2: clubes con acordeón de rúbricas ── */
const MobileClubsStep = ({ selectedEvent, reportData, onBack }) => {
  const [zoneFilter, setZoneFilter] = useState('');

  useEffect(() => {
    setZoneFilter('');
  }, [selectedEvent?._id]);

  const getUniqueZones = () =>
    [...new Set((reportData?.results || []).map(c => c.affiliationZone).filter(Boolean))].sort();

  const filteredResults = (reportData?.results || []).filter(club =>
    !zoneFilter || club.affiliationZone === zoneFilter
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Back bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexShrink: 0 }}>
        <IconButton size="small" onClick={onBack} sx={{ color: '#284B32' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#284B32', flex: 1 }} noWrap>
          {selectedEvent.name}
        </Typography>
        <Chip
          label={`Total: ${reportData.summary.totalGrades}`}
          size="small"
          sx={{ backgroundColor: '#E6F4EA', color: '#1b5e20', fontWeight: 700, fontSize: '0.68rem', height: 20 }}
        />
      </Box>

      {/* Summary metrics */}
      <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexShrink: 0 }}>
        {[
          { label: 'Clubes', value: reportData.summary.totalClubs },
          { label: 'Pts totales', value: reportData.summary.totalGrades },
          { label: 'Promedio', value: reportData.summary.averageGrade },
        ].map(m => (
          <Box key={m.label}>
            <Typography variant="caption" color="text.secondary" display="block">{m.label}</Typography>
            <Typography variant="body1" sx={{ fontWeight: 800, color: '#284B32', lineHeight: 1 }}>{m.value}</Typography>
          </Box>
        ))}
      </Box>

      {getUniqueZones().length > 1 && (
        <FormControl size="small" fullWidth sx={{ mb: 1.5, flexShrink: 0 }}>
          <InputLabel>Zona</InputLabel>
          <Select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)} label="Zona">
            <MenuItem value=""><em>Todas</em></MenuItem>
            {getUniqueZones().map(z => <MenuItem key={z} value={z}>{z}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      <Divider sx={{ mb: 1.5, flexShrink: 0 }} />

      {/* Clubs accordion */}
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {filteredResults.map((club, index) => (
          <Accordion
            key={club.clubId}
            disableGutters
            sx={{ mb: 1, borderRadius: 1, '&:before': { display: 'none' }, boxShadow: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, py: 0.5, minHeight: 48 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 20, flexShrink: 0 }}>
                  {index + 1}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                    {club.clubName}
                  </Typography>
                  {(club.affiliationName || club.affiliationZone) && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {[club.affiliationName, club.affiliationZone].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={club.totalGrade}
                  size="small"
                  sx={{ backgroundColor: '#E6F4EA', color: '#1b5e20', fontWeight: 700, fontSize: '0.65rem', height: 20, flexShrink: 0 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, pb: 1 }}>
              {club.rubricGrades.map((rubric, idx) => (
                <Box
                  key={idx}
                  sx={{
                    mx: 1.5,
                    mb: 0.75,
                    p: 1,
                    borderRadius: 1,
                    borderLeft: `3px solid ${rubric.isGraded ? '#4caf50' : '#ff9800'}`,
                    backgroundColor: rubric.isGraded ? '#f9fdf9' : '#fffbf5',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, flex: 1, lineHeight: 1.3 }}>
                      {rubric.rubricName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      {rubric.isGraded
                        ? <CheckIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                        : <PendingIcon sx={{ fontSize: 14, color: '#ff9800' }} />
                      }
                      <Typography variant="caption" sx={{ fontWeight: 700, color: rubric.isGraded ? '#284B32' : 'text.secondary' }}>
                        {rubric.grade}
                        <Typography component="span" variant="caption" color="text.secondary"> / {rubric.maxPoints}</Typography>
                      </Typography>
                    </Box>
                  </Box>
                  {rubric.savedBy && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {rubric.savedBy}
                    </Typography>
                  )}
                  {rubric.comments && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.25 }}>
                      {rubric.comments}
                    </Typography>
                  )}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export const GradesByEvents = () => {
  const { activeCamp } = useAuthContext();
  const { post } = useAxios();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [events, setEvents]               = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [reportData, setReportData]       = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [mobileStep, setMobileStep]       = useState('events'); // 'events' | 'clubs'

  // Reset on camp change
  useEffect(() => {
    setSelectedEvent(null);
    setReportData(null);
    setMobileStep('events');
    if (activeCamp?._id) {
      fetchEvents(activeCamp._id);
    } else {
      setEvents([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCamp?._id]);

  // Push a history entry when entering the clubs step on mobile
  useEffect(() => {
    if (!isMobile) return;
    if (mobileStep === 'clubs') {
      window.history.pushState({ mobileStep: 'clubs' }, '');
    }
  }, [mobileStep, isMobile]);

  // Listen for browser back button / swipe gesture
  useEffect(() => {
    if (!isMobile) return;
    const handlePop = () => {
      if (mobileStep === 'clubs') {
        setMobileStep('events');
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [mobileStep, isMobile]);

  const fetchEvents = async (campId) => {
    setLoadingEvents(true);
    try {
      const response = await axios.get(`${eventUrl.BASE_URL}/filter/${campId}`);
      let eventsData = response.data?.events || (Array.isArray(response.data) ? response.data : []);
      eventsData.sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));
      setEvents(eventsData);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoadingEvents(false);
    }
  };

  const fetchReport = async (event) => {
    setReportData(null);
    setLoadingReport(true);
    try {
      const response = await post(`${baseUrls.baseUrl}/reports/gradesByEvents`, {
        campId: activeCamp._id,
        eventId: event._id,
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      alert('Error al generar el reporte');
    } finally {
      setLoadingReport(false);
    }
  };

  const handleSelectEvent = (event) => {
    const alreadySelected = selectedEvent?._id === event._id;
    setSelectedEvent(event);
    if (isMobile) setMobileStep('clubs');
    if (!alreadySelected) fetchReport(event);
  };

  const handleMobileBack = () => {
    // Triggers popstate → handler sets mobileStep back to 'events'
    window.history.back();
  };

  return (
    <>
      <PrintStyles />
      <Container maxWidth="xl" sx={{
        pt: 3, pb: 2,
        ...(isMobile
          ? { p: 2 }
          : { height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
        ),
      }}>
        {/* Title */}
        <Box sx={{ pl: 2, borderLeft: '4px solid #284B32', mb: 2, flexShrink: 0, '@media print': { display: 'none' } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#284B32', lineHeight: 1.2, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
            Calificaciones por Evento
          </Typography>
        </Box>

        {!activeCamp?._id && (
          <Typography color="text.secondary">Selecciona un campamento para ver el reporte.</Typography>
        )}

        {activeCamp?._id && (
          isMobile ? (
            /* ══ MÓVIL: drill-down de 2 pasos ══ */
            <>
              {/* Paso 1: lista de eventos */}
              {mobileStep === 'events' && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, fontWeight: 600 }}>
                    {events.length} evento{events.length !== 1 ? 's' : ''} — toca para ver clubes
                  </Typography>
                  {loadingEvents ? (
                    <Box display="flex" justifyContent="center" mt={6}>
                      <CircularProgress sx={{ color: '#284B32' }} />
                    </Box>
                  ) : events.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" mt={4}>
                      No hay eventos disponibles
                    </Typography>
                  ) : (
                    events.map(event => (
                      <EventCard
                        key={event._id}
                        event={event}
                        selected={selectedEvent?._id === event._id}
                        onClick={() => handleSelectEvent(event)}
                        disabled={loadingReport}
                      />
                    ))
                  )}
                </Box>
              )}

              {/* Paso 2: clubes con acordeón */}
              {mobileStep === 'clubs' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
                  {loadingReport ? (
                    <Box display="flex" justifyContent="center" mt={6}>
                      <CircularProgress sx={{ color: '#284B32' }} />
                    </Box>
                  ) : reportData ? (
                    <MobileClubsStep
                      selectedEvent={selectedEvent}
                      reportData={reportData}
                      onBack={handleMobileBack}
                    />
                  ) : null}
                </Box>
              )}
            </>
          ) : (
            /* ══ DESKTOP: split layout ══ */
            <Grid container spacing={3} sx={{ flex: 1, minHeight: 0, '@media print': { display: 'block' } }}>
              {/* Left: events list */}
              <Grid item xs={12} md={4} sx={{ height: '100%', '@media print': { display: 'none' } }}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, fontWeight: 600, flexShrink: 0 }}>
                    {events.length} eventos
                  </Typography>
                  {loadingEvents ? (
                    <Box display="flex" justifyContent="center" mt={4}>
                      <CircularProgress sx={{ color: '#284B32' }} />
                    </Box>
                  ) : events.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" mt={4}>
                      No hay eventos disponibles
                    </Typography>
                  ) : (
                    <Box sx={{ overflowY: 'auto', flex: 1 }}>
                      {events.map(event => (
                        <EventCard
                          key={event._id}
                          event={event}
                          selected={selectedEvent?._id === event._id}
                          onClick={() => handleSelectEvent(event)}
                          disabled={loadingReport}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Right: report detail */}
              <Grid item xs={12} md={8} sx={{ height: '100%' }}>
                <Card sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  {!selectedEvent ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography color="text.disabled">Selecciona un evento para ver el detalle.</Typography>
                    </Box>
                  ) : loadingReport ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <CircularProgress sx={{ color: '#284B32' }} />
                    </Box>
                  ) : reportData ? (
                    <ReportDetail
                      selectedEvent={selectedEvent}
                      reportData={reportData}
                      activeCamp={activeCamp}
                    />
                  ) : null}
                </Card>
              </Grid>
            </Grid>
          )
        )}
      </Container>
    </>
  );
};

export default GradesByEvents;
